import { useEffect, useState } from 'react';
import { api } from '../../../api/client';
import CategoryForm from './CategoryForm';
import ExpenseForm from './ExpenseForm';
import styles from './ExpensesPage.module.css';

interface ExpenseCategory {
  id: string;
  name: string;
}

interface Expense {
  id: string;
  categoryId: string;
  amount: number;
  description: string;
  date: string;
  receiptUrl: string | null;
  receiptType: string;
  notes: string | null;
}

export default function ExpensesPage() {
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [categories, setCategories] = useState<ExpenseCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selected, setSelected] = useState<Expense | null>(null);
  const [showCatModal, setShowCatModal] = useState(false);
  const [selectedCat, setSelectedCat] = useState<ExpenseCategory | null>(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [exps, cats] = await Promise.all([
        api.get<any[]>('/api/expenses'),
        api.get<any[]>('/api/expenses/categories'),
      ]);
      setExpenses(Array.isArray(exps) ? exps : []);
      setCategories(Array.isArray(cats) ? cats : []);
    } catch {
      /* ignore */
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleDelete = async (id: string) => {
    if (!confirm('¿Eliminar este gasto?')) return;
    try {
      await api.delete(`/api/expenses/${id}`);
      fetchData();
    } catch {
      alert('Error al eliminar');
    }
  };

  const handleDeleteCategory = async (id: string) => {
    if (!confirm('¿Desactivar esta categoría?')) return;
    try {
      await api.delete(`/api/expenses/categories/${id}`);
      fetchData();
    } catch {
      alert('Error al desactivar');
    }
  };

  const openEditCat = (cat: ExpenseCategory) => {
    setSelectedCat(cat);
    setShowCatModal(true);
  };

  const categoryName = (catId: string) => categories.find((c) => c.id === catId)?.name ?? catId;

  const formatCurrency = (n: number) =>
    new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      maximumFractionDigits: 0,
    }).format(n);

  const formatDate = (d: string) => {
    const date = new Date(d);
    return date.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const receiptLabel = (type: string) => {
    switch (type) {
      case 'invoice':
        return 'Factura';
      case 'receipt_note':
        return 'Nota';
      default:
        return 'Sin comprobante';
    }
  };

  const openEdit = (exp: Expense) => {
    setSelected(exp);
    setShowModal(true);
  };

  const openCreate = () => {
    setSelected(null);
    setShowModal(true);
  };

  const total = expenses.reduce((sum, e) => sum + e.amount, 0);

  return (
    <div className={styles.container}>
      <div className={styles.topBar}>
        <div>
          <h1>Egresos</h1>
          <p className={styles.subtitle}>Registro de gastos y egresos</p>
        </div>
        <div className={styles.topActions}>
          <button
            className={styles.secondaryButton}
            onClick={() => {
              setSelectedCat(null);
              setShowCatModal(true);
            }}
          >
            + Categoría
          </button>
          <button className={styles.addButton} onClick={openCreate}>
            + Nuevo Gasto
          </button>
        </div>
      </div>

      <section className={styles.catSection}>
        <div className={styles.catHeader}>
          <h3>Categorías</h3>
          {categories.length > 0 && <span className={styles.catCount}>{categories.length}</span>}
        </div>
        <div className={styles.catList}>
          {categories.map((cat) => (
            <div key={cat.id} className={styles.catTag}>
              <span>{cat.name}</span>
              <button className={styles.catEditBtn} onClick={() => openEditCat(cat)} title="Editar">
                ✎
              </button>
              <button
                className={styles.catDelBtn}
                onClick={() => handleDeleteCategory(cat.id)}
                title="Eliminar"
              >
                ×
              </button>
            </div>
          ))}
          {categories.length === 0 && (
            <p className={styles.catEmpty}>Sin categorías. Creá una para empezar.</p>
          )}
        </div>
      </section>

      {loading ? (
        <p className={styles.loading}>Cargando...</p>
      ) : (
        <>
          <div className={styles.totalRow}>
            <strong>Total: {formatCurrency(total)}</strong>
          </div>

          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Categoría</th>
                  <th>Descripción</th>
                  <th>Monto</th>
                  <th>Comprobante</th>
                  <th>Notas</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {expenses.map((exp) => (
                  <tr key={exp.id}>
                    <td>{formatDate(exp.date)}</td>
                    <td>{categoryName(exp.categoryId)}</td>
                    <td>{exp.description}</td>
                    <td className={styles.amount}>{formatCurrency(exp.amount)}</td>
                    <td>{receiptLabel(exp.receiptType)}</td>
                    <td>{exp.notes || '-'}</td>
                    <td>
                      <button className={styles.actionBtn} onClick={() => openEdit(exp)}>
                        Editar
                      </button>
                      <button
                        className={styles.actionBtnDanger}
                        onClick={() => handleDelete(exp.id)}
                      >
                        Eliminar
                      </button>
                    </td>
                  </tr>
                ))}
                {expenses.length === 0 && (
                  <tr>
                    <td colSpan={7} className={styles.empty}>
                      No hay egresos registrados
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {showModal && (
        <ExpenseForm
          expense={selected}
          categories={categories}
          onClose={() => setShowModal(false)}
          onSuccess={() => {
            setShowModal(false);
            fetchData();
          }}
        />
      )}

      {showCatModal && (
        <CategoryForm
          category={selectedCat}
          onClose={() => setShowCatModal(false)}
          onSuccess={() => {
            setShowCatModal(false);
            fetchData();
          }}
        />
      )}
    </div>
  );
}
